import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { PATHS } from 'routes/routes.constants';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';
import Text from '@polyai/ui/components/atoms/Text';

import api from 'api';
import { AssistantType } from 'api/resources/accounts/types';
import useAccounts from 'hooks/useAccounts';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { generateNextPath } from 'lib/routeHelper';
import { usePersonalAccount } from 'screens/AgentWizard/hooks/usePersonalAccount';

const ConversationShortcut: React.FC = () => {
  const { isFeatureOn } = useIsFeatureOn();
  const { replaceUrl } = useRouter();
  const isPortalProjectsFeatureOn = isFeatureOn('portal_projects_in_jupiter');

  const { accounts, isLoadingAccounts } = useAccounts();
  const accountId = usePersonalAccount('');

  // Fetch projects for the account
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useQuery({
    queryKey: [accountId, 'projects'],
    queryFn: () => api.getProjects(accountId!),
    enabled: !!accountId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Get the first project
  const firstProject = projectsData?.projects
    ? (() => {
        const filteredProjects = isPortalProjectsFeatureOn
          ? projectsData.projects
          : projectsData.projects.filter(
              project => project.type !== AssistantType.INTENT,
            );
        return filteredProjects[0];
      })()
    : null;

  // Redirect when we have both account and project
  useEffect(() => {
    if (accountId && firstProject) {
      const path = generateNextPath(PATHS.LATEST_CONVERSATION, {
        accountId,
        projectId: firstProject.id,
      });
      replaceUrl(path);
    }
  }, [accountId, firstProject, replaceUrl]);

  if (isLoadingAccounts || isLoadingProjects) {
    return <Loader fullScreen logo />;
  }

  if (projectsError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">Failed to load account information</Text>
      </div>
    );
  }

  if (!accounts.length || !accountId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">No accounts found</Text>
      </div>
    );
  }

  if (!firstProject) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">No projects found</Text>
      </div>
    );
  }

  return <Loader fullScreen logo />;
};

export default ConversationShortcut;
